import { RESEND_COOL_DOWN } from "@/config/config";
import privateConfig from "@/config/private.config";
import publicConfig from "@/config/public.config";
import { createClient } from "@/utils/supabase/client";
import type { JwtPayload } from "@supabase/supabase-js";
import { createHash, randomBytes } from "crypto";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { cache } from "react";

dayjs.extend(utc);

const VERIFICATION_EXPIRY_HOURS = 24;

function createVerificationToken(): { token: string; hash: string } {
  const token = randomBytes(32).toString("hex");
  const hash = createHash("sha256").update(token).digest("hex");
  return { token, hash };
}

export type AuthClaims = JwtPayload | null;

const getAuthClaims = cache(async () => {
  const supabase = await createClient();
  const { data, error } = await supabase.auth.getClaims();
  return { claims: data?.claims ?? null, error };
});

export const auth = cache(async () => {
  return getAuthClaims();
});

export const getAuthProfile = cache(async () => {
  const { claims, error } = await getAuthClaims();

  if (error || !claims?.sub) {
    return {
      profile: null,
      claims: null,
      error: error ?? new Error("Unauthorized"),
    };
  }

  const { getProfile } = await import("@/actions/profile/get-profile");

  const { data, error: profileError } = await getProfile(claims.sub);

  return {
    profile: data,
    claims,
    error: profileError,
  };
});

export async function sendVerificationEmail(
  userId: string,
  email: string,
): Promise<{ error: Error | null }> {
  const supabase = await createClient();
  const { token, hash } = createVerificationToken();
  const expiresAt = dayjs.utc().add(VERIFICATION_EXPIRY_HOURS, "hour")
    .toISOString();

  const { error: updateError } = await supabase
    .from("profiles")
    .update({
      email_confirmation_sent_at: dayjs.utc().toISOString(),
      email_confirmation_token_hash: hash,
      email_confirmation_expires_at: expiresAt,
    })
    .eq("id", userId);

  if (updateError) {
    return { error: new Error(updateError.message) };
  }

  const verifyUrl = `${publicConfig.baseUrl}/auth/verify-email?userId=${
    encodeURIComponent(userId)
  }&token=${encodeURIComponent(token)}`;
  const html = `
    <p>Please confirm your email address by clicking the link below.</p>
    <p><a href="${verifyUrl}">Confirm my email</a></p>
    <p>This link expires in ${VERIFICATION_EXPIRY_HOURS} hours. If you didn't create an account, you can ignore this email.</p>
  `;

  const { data, error } = await supabase.functions.invoke("send-email", {
    body: {
      from: privateConfig.smtp.from,
      fromName: privateConfig.smtp.fromName,
      to: email,
      subject: "Confirm your email",
      html,
      text:
        `Please confirm your email by visiting: ${verifyUrl}\n\nThis link expires in ${VERIFICATION_EXPIRY_HOURS} hours.`,
    },
  });

  if (error) {
    return {
      error: (data as { error?: string })?.error ?? error.message ??
        "Failed to send email.",
    };
  }

  return { error: null };
}

export async function resendConfirmationEmail(): Promise<
  { error: Error | null }
> {
  const { profile, error } = await getAuthProfile();

  if (error) {
    return {
      error,
    };
  }

  if (!profile?.email) {
    return {
      error: new Error("Email not found"),
    };
  }

  if (profile.email_confirmation_sent_at) {
    const sentAt = dayjs.utc(profile.email_confirmation_sent_at);
    const now = dayjs.utc();
    const diff = now.diff(sentAt, "seconds");
    if (diff < RESEND_COOL_DOWN) {
      return {
        error: new Error(
          "You can only resend the confirmation email every 30 seconds",
        ),
      };
    }
  }

  if (profile.email_confirmed_at) {
    return {
      error: new Error("Email already confirmed"),
    };
  }

  return sendVerificationEmail(profile.id, profile.email);
}
